/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ManualCheckModel} from './models/manual-check.model';
import {ConfirmModalConfigurationModel} from './models/confirm-modal-configuration.model';
import {InputTextareaComponent} from '../../input/input-textarea/input-textarea.component';
import {Subscription} from 'rxjs';

@Component({
    selector: 'app-confirm-modal',
    templateUrl: './confirm-modal.component.html',
    styleUrls: ['./confirm-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConfirmModalComponent implements OnInit, OnDestroy {

    @Input() modalConfiguration: ConfirmModalConfigurationModel = <ConfirmModalConfigurationModel>{};
    @Output() primaryEvent: EventEmitter<void | string> = new EventEmitter<void | string>();
    @Output() secondaryEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() closeEvent: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('inputTextArea') inputTextArea: InputTextareaComponent;

    public manualChecks: ManualCheckModel[] = [];
    private notes?: string;
    private subscriptions = new Subscription();

    // General modal buttons width = 100px.
    // General modal padding that surrounds the footer = 15px;
    // General modal min-width = (100px * 2) + (15px *2)
    private readonly GENERAL_MODAL_MIN_WIDTH: number = 230;

    // Default confirmation window width = 420px.
    // General modal padding that surrounds the modal body = 20px.
    private readonly GENERAL_MODAL_BODY_PADDING: number = 40;
    // Body default width = 420px - (20px * 2)
    private readonly DEFAULT_WIDTH: number = 380;

    private readonly NOTES_DEFAULT_HEIGHT: number = 100;

    get showModalBodyTitle(): boolean {
        return this.bodyConfigurationIsNotNull && this.modalConfiguration.body.title != null;
    }

    get showModalBodySubtitle(): boolean {
        return this.bodyConfigurationIsNotNull && this.modalConfiguration.body.subtitle != null;
    }

    get showModalBodyManualChecks(): boolean {
        return this.bodyConfigurationIsNotNull
            && this.modalConfiguration.body.manualChecks != null
            && this.modalConfiguration.body.manualChecks.options != null
            && this.modalConfiguration.body.manualChecks.options.length > 0;
    }

    get showModalCheckTitle(): boolean {
        return this.modalConfiguration.body.manualChecks?.title != null;
    }

    get showNotes(): boolean {
        return this.bodyConfigurationIsNotNull && this.modalConfiguration.body.notes != null;
    }

    get labelNotes(): boolean {
        return this.modalConfiguration != null &&
            this.modalConfiguration.body != null &&
            this.modalConfiguration.body.notes != null &&
            this.modalConfiguration.body.notes.labelNotes != null;
    }

    get modalBodyWidth(): string {
        let width: number = this.GENERAL_MODAL_MIN_WIDTH;

        if (this.bodyConfigurationIsNotNull && this.modalConfiguration.body.width != null) {
            width = this.modalConfiguration.body.width < width ? width : this.modalConfiguration.body.width;
        } else {
            width = this.DEFAULT_WIDTH;
        }

        return (width - this.GENERAL_MODAL_BODY_PADDING).toString();
    }

    get notesTextareaHeight(): number {
        let height: number = this.NOTES_DEFAULT_HEIGHT;
        if (this.bodyConfigurationIsNotNull
            && this.modalConfiguration.body.notes != null
            && this.modalConfiguration.body.notes.height != null) {
            height = this.modalConfiguration.body.notes.height;
        }
        return height;
    }

    get enablePrimaryButton(): boolean {
        let enable = true;
        if (this.manualChecks != null) {
            enable = this.manualChecks.every((item) => item.checked);
        }
        if (enable && (this.notes == null || this.notes.length === 0)
            && this.bodyConfigurationIsNotNull
            && this.modalConfiguration.body.notes != null
            && this.modalConfiguration.body.notes.isNotesMandatory) {
            enable = false;
        }
        return enable;
    }

    private get bodyConfigurationIsNotNull(): boolean {
        return this.modalConfiguration != null && this.modalConfiguration.body != null;
    }

    constructor(public activeModal: NgbActiveModal) {
    }

    ngOnInit(): void {
        this.mapManualChecks();
        this.notes = this.modalConfiguration?.body?.notes?.notesValue;

        this.subscriptions.add(this.modalConfiguration?.isLoading?.subscribe((isLoading) =>
                isLoading ? this.inputTextArea?.disable() : this.inputTextArea.enable()
        ));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    toggleManualCheck(manualCheck: ManualCheckModel): void {
        manualCheck.checked = !manualCheck.checked;
    }

    confirm(): void {
        if (this.notes != null && this.notes.length > 0) {
            this.primaryEvent.emit(this.notes);
        } else {
            this.primaryEvent.emit();
        }
    }

    close(): void {
        this.closeEvent.emit();
    }

    cancel(): void {
        this.secondaryEvent.emit();
    }

    notesValueChange(notes: string): void {
        this.notes = notes;
    }

    /**
     * Checks if body subtitle is a string
     * @returns boolean
     */
    isSubtitleString(): boolean {
        return this.modalConfiguration?.body?.subtitle?.constructor === String
                || this.modalConfiguration?.body?.subtitle?.constructor === Array
                && this.modalConfiguration?.body?.subtitle?.length === 1;
    }

    private mapManualChecks(): void {
        if (this.bodyConfigurationIsNotNull && this.modalConfiguration.body.manualChecks != null) {
            this.manualChecks = this.modalConfiguration.body.manualChecks.options.map((item) => {
                const optionId = `confirm-modal-manual-check-${this.modalConfiguration?.body?.manualChecks?.options.indexOf(item)}`;
                return {text: item, checked: false, id: optionId};
            });
        }
    }

}
